/**
 * @file index.tsx
 * @author liuchen(loocliu@tencent.com)
 */
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Tabs, TabValue, Textarea } from 'tdesign-react';

import { IPhase } from '@/interface';

import styles from './style/index.module.less';

interface IItemProps {
  value: IPhase[];
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: IPhase[]) => void
}

const random = () => `array_${dayjs().valueOf()}`;

const Item = (props: IItemProps) => {
  const { value, disabled, onChange } = props;

  const [tabs, setTabs] = useState<{ value: string, label: string, content: string, removable: boolean }[]>([]);
  const [cur, setCur] = useState<TabValue>(0);
  const curTabIdx = useMemo(() => tabs.findIndex(({ value }) => value === cur), [tabs, cur]);

  useEffect(() => {
    if (tabs.length === 0) {
      const tabs = value.map(({ character }, idx) => ({
        value: `${random()}_${idx + 1}`,
        label: `人设${idx + 1}`,
        content: character,
        removable: value.length !== 1,
      }));
      setTabs(tabs);
      setCur(tabs[0].value);
    }
  }, [value, tabs]);

  const handleChange = useCallback((val: string) => {
    setTabs((pre) => {
      const newTabs = pre.map(item => ({ ...item, content: item.value === cur ? val : item.content }));
      onChange(newTabs.map(({ content }) => ({ character: content })));
      return newTabs;
    });
  }, [cur]);

  return <div className={styles.root}>
    <Tabs
      placement={'top'}
      size={'medium'}
      style={{ width: '100%' }}
      disabled={disabled}
      theme={'card'}
      defaultValue={1}
      value={cur}
      onChange={(v) => {
        if (v === undefined) {
          return;
        }
        setCur(v);
      }}
      addable
      list={tabs}
      onRemove={(item) => {
        if (disabled) {
          return;
        }
        if (tabs.length <= 1) {
          return;
        }
        onChange(value.filter((_, idx) => idx !== item.
          index));
        if (tabs.length === 2) {
          setTabs(pre => pre.filter(item2 => item2.value !== item.value).map(item => ({ ...item, label: '人设1', removable: false })));
        } else {
          setTabs(pre => pre.filter(item2 => item2.value !== item.value).map((item, idx) => ({ ...item, label: `人设${idx + 1}` })));
        }
        if (curTabIdx === item.index) {
          if (curTabIdx - 1 >= 0) {
            const lastTab = tabs[curTabIdx - 1];
            setCur(lastTab.value);
          }
          if (curTabIdx === 0) {
            const nextTab = tabs[curTabIdx + 1];
            if (nextTab) {
              setCur(nextTab.value);
            }
          }
        }
      }}
      onAdd={() => {
        if (disabled) {
          return;
        }
        setTabs(pre => [...pre.map(item => ({ ...item, removable: true })), {
          value: `${random()}_${pre.length + 1}`,
          label: `人设${pre.length + 1}`,
          content: '',
          removable: true,
        }]);
        onChange([...value, { character: '' }]);
      }}
    >
    </Tabs >
    <Textarea
      disabled={disabled}
      placeholder={props.placeholder}
      style={{ marginTop: 1 }}
      autosize={{ maxRows: 5, minRows: 5 }}
      value={tabs[curTabIdx]?.content || ''}
      onChange={handleChange}
    />
  </div>;
};

export default Item;
